import { z } from "zod"

export type ConfirmationConfig = z.infer<typeof confirmationSchema>
export type ButtonConfig<T extends z.ZodTypeAny> = z.infer<ReturnType<typeof actionButtonSchema<T>>>
export type DefaultAction = z.infer<typeof defaultActionSchema>

const notificationSchema = z.object({
  onSuccess: z.string().optional(),
  onFailure: z.string().optional(),
})

const startRuleActionSchema = z.object({
  type: z.literal('start-rule'),
  startRule: z.string(),
  parameters: z.record(z.string(), z.string()).optional(),
  timestampVariable: z.string().optional(),
})

const publishAppVarSchema = z.object({
  type: z.literal('publish'),
  topic: z.string(),
  value: z.string(),
})

const baseActionSchema = z.object({
  notification: notificationSchema.optional(),
})

const confirmationSchema = z.object({
  message: z.string().optional(),
  yesLabel: z.string().optional(),
  yesIcon: z.string().optional(),
  yesColor: z.string().optional(),
  noLabel: z.string().optional(),
  noIcon: z.string().optional(),
  noColor: z.string().optional(),
})

export const actionSchema = <
  T extends readonly z.ZodDiscriminatedUnionOption<"type">[]
>(
  ...extraActions: T
) =>
  z.discriminatedUnion("type",
    [ startRuleActionSchema, publishAppVarSchema, ...extraActions ])
    .and(baseActionSchema)

export const defaultActionSchema = actionSchema()

export function actionButtonSchema<T extends z.ZodTypeAny>(actionSchema: T) {
  type Button = {
    icon?: string
    label?: string
    actions?: z.infer<T>[]
    buttons?: Button[]
    confirmation?: z.infer<typeof confirmationSchema>
    hidden?: boolean | string
    disabled?: boolean | string
    separator?: boolean
  }

  const schema: z.ZodType<Button> = z.lazy(() =>
    z.object({
      icon: z.string().optional(),
      label: z.string().optional(),
      actions: z.array(actionSchema).optional(),
      buttons: z.array(schema).optional(),
      confirmation: confirmationSchema.optional(),
      hidden: z.union([z.boolean(), z.string()]).optional(),
      disabled: z.union([z.boolean(), z.string()]).optional(),
      separator: z.boolean().optional(),
    })
  )
  return schema
}
